import type { Locale } from "@/lib/api";

export const POPULAR_IMPORT_BRANDS_TITLE: Record<Locale, string> = {
  en: "Popular brands for import calculation",
  ru: "Популярные марки для расчёта ввоза",
};

// UI-строки, вшитые в компоненты (не приходят из API). Снято с прода EN/RU.
export const STR = {
  en: {
    year: "Year",
    mileage: "Mileage",
    power: "Power",
    engine: "Engine",
    details: "Details",
    buyHome: "Enquire",
    buyCatalog: "Buy",
    enquire: "Enquire",
    commissionCta: "Commission yours",
    commissioned: "Commissioned",
    noMileage: "No mileage",
    type: "Type",
    electric: "Electric",
    inStock: "In stock",
    onOrder: "To order",
    sold: "Sold",
    readMore: "Read more",
    reviewsTitle: "What our clients say",
    reviewsAll: "All reviews",
    reviewsSource: "Source",
    dash: "—",
  },
  ru: {
    year: "Год",
    mileage: "Пробег",
    power: "Мощность",
    engine: "Объём",
    details: "Подробнее",
    buyHome: "Подобрать",
    buyCatalog: "Купить авто",
    enquire: "Связаться",
    commissionCta: "Подобрать",
    commissioned: "Спецзаказ",
    noMileage: "Без пробега",
    type: "Тип",
    electric: "Электро",
    inStock: "В наличии",
    onOrder: "Под заказ",
    sold: "Продано",
    readMore: "Читать",
    reviewsTitle: "Отзывы наших заказчиков",
    reviewsAll: "Все отзывы",
    reviewsSource: "Источник",
    dash: "—",
  },
} as const;

export function t(locale: Locale) {
  return STR[locale] ?? STR.en;
}

// Каталог: заголовки страниц/категорий, фильтры, строка поиска, контакт-блок.
export const CATALOG = {
  en: {
    allCars: "Cars for Sale in Cyprus",
    breadcrumbCatalog: "Catalog",
    catInCyprus: "Cars in Cyprus",
    catCommissioned: "Commissioned Cars",
    catInTransit: "Cars in Transit",
    catToOrder: "Cars by Order",
    filters: "Filters",
    selectCategory: "Select a category",
    selectBrand: "Select a brand",
    selectYear: "Select year",
    selectFeatures: "Select features",
    searchDots: "Search..",
    selected: "selected",
    showAll: "Show all filters",
    hideFilters: "Hide filters",
    apply: "Search",
    reset: "Reset",
    searchBarPlaceholder: "Search by brand, model",
    searchBarButton: "Search",
    prev: "Previous",
    next: "Next",
    noMatch: "No vehicles match your filters.",
    groups: {
      category: "Category",
      brand: "Brands",
      year: "Year",
      drive: "Drive",
      transmission: "Transmission",
      fuel: "Fuel type",
      price: "Price, EUR",
      mileage: "Mileage, km",
      power: "Power, hp",
      engine: "Engine volume, L",
      color: "Color",
      bodyType: "Body type",
      condition: "Condition",
      steering: "Steering",
      feature: "Features",
    } as Record<string, string>,
  },
  ru: {
    allCars: "Каталог автомобилей на Кипре",
    breadcrumbCatalog: "Каталог",
    catInCyprus: "Автомобили в наличии на Кипре",
    catCommissioned: "Доставленные автомобили",
    catInTransit: "Автомобили в пути",
    catToOrder: "Автомобили под заказ",
    filters: "Фильтры",
    selectCategory: "Выберите категорию",
    selectBrand: "Выберите бренд",
    selectYear: "Выберите год",
    selectFeatures: "Выберите опции",
    searchDots: "Поиск..",
    selected: "выбрано",
    showAll: "Показать все фильтры",
    hideFilters: "Скрыть фильтры",
    apply: "Подобрать",
    reset: "Сбросить",
    searchBarPlaceholder: "Поиск по марке, модели",
    searchBarButton: "Поиск",
    prev: "Предыдущая",
    next: "Следующая",
    noMatch: "Нет автомобилей по вашим фильтрам.",
    groups: {
      category: "Категория",
      brand: "Бренды",
      year: "Год",
      drive: "Привод",
      transmission: "Тип КПП",
      fuel: "Тип топлива",
      price: "Цена, EUR",
      mileage: "Пробег, км",
      power: "Мощность, л.с.",
      engine: "Объем, л",
      color: "Цвет",
      bodyType: "Тип кузова",
      condition: "Состояние",
      steering: "Руль",
      feature: "Опции",
    } as Record<string, string>,
  },
} as const;

export function tc(locale: Locale) {
  return CATALOG[locale] ?? CATALOG.en;
}

// Бренды (/brands и /brands/<slug>).
export const BRANDS = {
  en: {
    heroTitle: "Explore by brand",
    heroSubtitle:
      "Cars by brand — a portfolio of commissioned and sold cars, and those in stock in Cyprus right now.",
    breadcrumb: "Brands",
    carsTitle: "Cars in Cyprus",
    emptyState: "No cars for this brand yet",
    view: (title: string) => `View ${title}`,
    modelH1: (title: string) => `${title} Cars`,
  },
  ru: {
    heroTitle: "Марки автомобилей",
    heroSubtitle:
      "Автомобили по маркам — портфолио автомобилей, доставленных под заказ и проданных, а также в наличии на Кипре прямо сейчас.",
    breadcrumb: "Марки",
    carsTitle: "Автомобили на Кипре",
    emptyState: "Пока нет автомобилей этой марки",
    view: (title: string) => `Все ${title}`,
    modelH1: (title: string) => `Автомобили ${title}`,
  },
} as const;

export function tb(locale: Locale) {
  return BRANDS[locale] ?? BRANDS.en;
}

// Карточка авто (/cars-cyprus/<slug>).
export const DETAIL = {
  en: {
    breadcrumbCatalog: "Catalog",
    specifications: "Specifications",
    options: "Options",
    description: "Description",
    related: "Related cars",
    reviews: "Customer reviews",
    faq: "Frequently asked questions",
    enquire: "Enquire",
    commissionCta: "Commission yours",
    shield: "Car verified and ready for sale",
    commissionedShield: "Vehicle imported and delivered to customer",
    noMileage: "No mileage",
    commissioned: "Commissioned",
    sold: "Sold",
    co2Unit: "g/km",
    taxPerYear: (v: number) => `${formatMoneyValue(v, "en")}/year`,
    specLabels: {
      brand: "Brand",
      model: "Model",
      year: "Year",
      mileage: "Mileage",
      power: "Power",
      volume: "Volume",
      co2: "CO₂",
      roadTax: "Road Tax",
      fuel: "Fuel",
      color: "Body Color",
      drive: "Drive",
      transmission: "Transmission",
    },
  },
  ru: {
    breadcrumbCatalog: "Каталог",
    specifications: "Характеристики",
    options: "Опции",
    description: "Описание",
    related: "Похожие авто",
    reviews: "Отзывы наших клиентов",
    faq: "Частозадаваемые вопросы",
    enquire: "Связаться",
    commissionCta: "Подобрать",
    shield: "Авто проверено и готово к продаже",
    commissionedShield: "Автомобиль импортирован и передан заказчику",
    noMileage: "Нет пробега",
    commissioned: "Спецзаказ",
    sold: "Продано",
    co2Unit: "г/км",
    taxPerYear: (v: number) => `${formatMoneyValue(v, "ru")}/год`,
    specLabels: {
      brand: "Марка",
      model: "Модель",
      year: "Год",
      mileage: "Пробег",
      power: "Мощность",
      volume: "Объём",
      co2: "CO₂",
      roadTax: "Дорожный налог",
      fuel: "Топливо",
      color: "Цвет кузова",
      drive: "Привод",
      transmission: "Тип КПП",
    },
  },
} as const;

export function td(locale: Locale) {
  return DETAIL[locale] ?? DETAIL.en;
}

// Блог/новости/гайды.
export const BLOG = {
  en: {
    readMore: "Read more",
    prev: "Previous",
    next: "Next",
    back: "Back to blog",
    backNews: "Back to news",
    backGuides: "Back to guides",
    share: "Share article",
    newsTitle: "News & Insights",
    newsSubtitle:
      "Expert reviews, market updates, and everything you need to know before commissioning your next vehicle.",
    guidesTitle: "Guides",
    guidesSubtitle:
      "Practical guides for importing, registering and owning cars in Cyprus.",
  },
  ru: {
    readMore: "Подробнее",
    prev: "Предыдущая",
    next: "Следующая",
    back: "Вернуться назад",
    backNews: "К новостям",
    backGuides: "К автолайфхакам",
    share: "Поделиться статьёй",
    newsTitle: "Автомобильные новости мира авто",
    newsSubtitle:
      "Последние обзоры, анонсы и экспертиза — всё, что важно знать владельцам и любителям автомобилей на Кипре и за его пределами.",
    guidesTitle: "Автолайфхаки",
    guidesSubtitle:
      "Практичные материалы по импорту, регистрации и владению авто на Кипре.",
  },
} as const;

export function tg(locale: Locale) {
  return BLOG[locale] ?? BLOG.en;
}

const numLocale = (l: Locale) => (l === "ru" ? "ru-RU" : "en-US");

export function fmtInt(n: number | string, locale: Locale): string {
  return Number(n).toLocaleString(numLocale(locale));
}

function formatMoneyValue(n: number | string, locale: Locale): string {
  return `€${fmtInt(n, locale)}`;
}

export function fmtMoney(n: number | string, locale: Locale): string {
  return formatMoneyValue(n, locale);
}

// Значения карточек с единицами, как на проде.
export function fmtYear(y: number | string, locale: Locale): string {
  return locale === "ru" ? `${y} г.` : `${y}`;
}
export function fmtMileage(km: number | null, locale: Locale): string {
  if (km == null) return STR[locale].noMileage;
  return locale === "ru"
    ? `${fmtInt(km, locale)} км`
    : `${fmtInt(km, locale)} km`;
}
export function fmtPower(hp: number | null, locale: Locale): string {
  if (hp == null) return STR[locale].dash;
  return locale === "ru" ? `${hp} л.с.` : `${hp} hp`;
}
export function fmtEngine(l: number | string | null, locale: Locale): string {
  const n = Number(l);
  if (l == null || !Number.isFinite(n) || n <= 0) return STR[locale].dash;
  const value = n.toFixed(1);
  return locale === "ru" ? `${value.replace(".", ",")} л` : `${value} L`;
}
